import React, { useState, useEffect, useMemo, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Tutorial } from "@/assets";
import { Plus, Trash2, Box } from "lucide-react";
import {
  ProcessRoleOutputDTO,
  ProcessRoleFieldAdditionalData,
} from "../../processRole/types";
import { FieldLayout } from "./FieldLayout";
import ConfirmationDialog from "@components/ui/ConfirmationDialog";
import { maskCPF, maskPhone, isValidCPF, isValidEmail, isValidPhone } from '@/utils/maskUtils';
import { toast } from "sonner";

interface ProcessRoleFieldProps {
  processRole: ProcessRoleOutputDTO;
  value: Record<string, any> | Record<string, any>[];
  onChange: (value: Record<string, any> | Record<string, any>[]) => void;
  hasErrors?: boolean;
  multiline?: boolean;
  videoUrl?: string;
  label?: string;
  description?: string;
  activeStep?: number;
  totalSteps?: number;
  onValidationChange?: (isValid: boolean) => void;
}

// Tab navigation component for process role items
const ProcessRoleTabNavigation = ({
  items,
  activeIndex,
  onSelectIndex,
  onAddItem,
  onRemoveItem,
  roleName,
}: {
  items: Record<string, any>[];
  activeIndex: number;
  onSelectIndex: (index: number) => void;
  onAddItem: () => void;
  onRemoveItem: (index: number) => void;
  roleName: string;
}) => {
  return (
    <div className="flex flex-col-reverse sm:flex-row justify-center items-center gap-1 sm:gap-4 w-full relative mb-6 pb-4">
      <div className="flex gap-2 overflow-x-auto max-w-full pb-2">
        {items.map((_, index) => (
          <div
            key={index}
            onClick={() => onSelectIndex(index)}
            className="flex flex-col justify-between items-center min-w-[35px] h-[49px] cursor-pointer"
          >
            <div
              className={`flex items-center justify-center w-[35px] h-[35px] rounded-md ${
                index === activeIndex ? "bg-primary" : "bg-background/10"
              }`}
            >
              <span className="text-background text-xs sm:text-base">
                {(index + 1).toString().padStart(2, "0")}
              </span>
            </div>

            {/* Indica o item atual */}
            {index === activeIndex && (
              <span className="w-full h-[5px] rounded-xs bg-primary" />
            )}
          </div>
        ))}
      </div>

      {/* Botões de adicionar e remover */}
      <div className="flex self-end gap-2 sm:absolute right-0 top-0">
        {items.length > 1 && (
          <Button
            onClick={() => onRemoveItem(activeIndex)}
            variant="destructive"
            className="h-[35px] text-background rounded-md bg-background/10 hover:bg-background/10 my-0 py-0 has-[>svg]:px-2"
          >
            <Trash2 size={16} />
          </Button>
        )}
        <Button
          onClick={onAddItem}
          className="flex items-center justify-center h-[35px] w-[35px] p-0 bg-primary hover:bg-primary/90 rounded-md"
          title="Adicionar novo item"
        >
          <Plus size={16} />
        </Button>
      </div>
    </div>
  );
};

export const ProcessRoleField: React.FC<ProcessRoleFieldProps> = ({
  processRole,
  value,
  onChange,
  hasErrors,
  multiline = false,
  videoUrl,
  label,
  description,
  activeStep,
  totalSteps,
  onValidationChange,
}) => {
  // Converter para array se for uma única pessoa
  const itemsArray = multiline
    ? Array.isArray(value)
      ? value
      : [value || {}]
    : [value as Record<string, any>];

  const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [touched, setTouched] = useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  // Referência para controlar atualizações e evitar loops infinitos
  const isValidatingRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const lastValueRef = useRef(value);

  // ADD useEffect for initialization
  useEffect(() => {
    if (multiline) {
      // Original condition: itemsArray.length === 0, which means value is `[]`.
      if (Array.isArray(value) && value.length === 0) {
        onChange([{}]);
      }
    } else { // not multiline
      // Original condition: Object.keys(value || {}).length === 0 (true if value is undefined, null, or {}).
      // To prevent loops if onChange({}) results in value becoming {},
      // only call onChange if value is undefined or null.
      if (value === undefined || value === null) {
        onChange({});
      }
    }
  }, [multiline, value, onChange]);

  // Função para verificar se o item atual tem todos os campos obrigatórios preenchidos
  const isCurrentItemValid = () => {
    if (!processRole || !processRole.additionalData) return true;

    // Filtrar campos obrigatórios
    const requiredFields = processRole.additionalData.filter(
      (field) => field.required === true
    );

    // Se não houver campos obrigatórios, retorna true
    if (requiredFields.length === 0) return true;

    // Verificar se todos os campos obrigatórios estão preenchidos no item ativo
    const activeItem = itemsArray[activeItemIndex] || {};

    // Lista de campos inválidos
    const missingFields = requiredFields
      .filter((field) => {
        const fieldName = field.name || "";
        const fieldId = field._id || "";
        // Verificar tanto pelo name quanto pelo _id
        const fieldValue = activeItem[fieldName] || activeItem[fieldId];
        return (
          !fieldValue ||
          (typeof fieldValue === "string" && fieldValue.trim() === "")
        );
      })
      .map((field) => field.name || "");

    // Atualizar estado de campos inválidos apenas se houver mudança
    const currentInvalidFieldsStr = JSON.stringify(invalidFields.sort());
    const newInvalidFieldsStr = JSON.stringify([...missingFields].sort());

    if (currentInvalidFieldsStr !== newInvalidFieldsStr) {
      setInvalidFields(missingFields as string[]);

      // Notificar o componente pai sobre o estado de validação se houver mudança
      if (onValidationChange) {
        onValidationChange(missingFields.length === 0);
      }
    }

    return missingFields.length === 0;
  };

  // Validar todos os itens (útil quando o formulário é submetido)
  const validateAllItems = () => {
    if (!processRole || !processRole.additionalData) {
      if (onValidationChange) {
        onValidationChange(true);
      }
      return true;
    }

    // Se for multiline e não tiver itens, é inválido
    if (multiline && (!itemsArray || itemsArray.length === 0)) {
      if (onValidationChange) {
        onValidationChange(false);
      }
      return false;
    }

    // Filtrar campos obrigatórios
    const requiredFields = processRole.additionalData.filter(
      (field) => field.required === true
    );

    // Se não houver campos obrigatórios, retorna true
    if (requiredFields.length === 0) {
      if (onValidationChange) {
        onValidationChange(true);
      }
      return true;
    }

    // Verificar cada item
    let allItemsValid = true;
    let foundInvalid = false;

    for (let i = 0; i < itemsArray.length; i++) {
      const item = itemsArray[i] || {};

      // Verificar campos obrigatórios para este item
      const missingFields = requiredFields.filter((field) => {
        const fieldName = field.name || "";
        const fieldId = field._id || "";
        // Verificar tanto pelo name quanto pelo _id
        const fieldValue = item[fieldName] || item[fieldId];
        return (
          !fieldValue ||
          (typeof fieldValue === "string" && fieldValue.trim() === "")
        );
      });

      if (missingFields.length > 0) {
        allItemsValid = false;
        foundInvalid = true;

        // Encontrou item inválido, atualiza o índice ativo e os campos inválidos apenas se necessário
        if (activeItemIndex !== i) {
          setActiveItemIndex(i);
        }

        const newInvalidFields = missingFields.map(
          (field) => field.name || ""
        ) as string[];
        const currentInvalidFieldsStr = JSON.stringify(invalidFields.sort());
        const newInvalidFieldsStr = JSON.stringify(
          [...newInvalidFields].sort()
        );

        if (currentInvalidFieldsStr !== newInvalidFieldsStr) {
          setInvalidFields(newInvalidFields);
        }

        break; // Sai do loop ao encontrar o primeiro item inválido
      }
    }

    // Notificar o componente pai sobre o estado de validação
    if (onValidationChange) {
      onValidationChange(allItemsValid);
    }

    // Se não encontrou nenhum item inválido, limpa a lista de campos inválidos
    if (!foundInvalid) {
      setInvalidFields([]);
    }

    return allItemsValid;
  };

  // Efeito para validar sempre que o valor muda
  useEffect(() => {
    // Evitar validação na montagem inicial
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    // Verificar se o valor realmente mudou
    if (JSON.stringify(lastValueRef.current) === JSON.stringify(value)) {
      return;
    }

    lastValueRef.current = value;

    if (!isValidatingRef.current) {
      const timer = setTimeout(() => {
        isValidatingRef.current = true;
        const isValid = validateAllItems();

        // Sempre notificar o componente pai sobre o estado de validação após uma mudança de valor
        if (onValidationChange) {
          onValidationChange(isValid);
        }

        isValidatingRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [value]);

  // Efeito para validar quando o componente for montado
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isValidatingRef.current) {
        isValidatingRef.current = true;
        // Validar todos os itens quando o componente for montado
        const isValid = validateAllItems();

        // Notificar o componente pai sobre o estado de validação
        if (onValidationChange) {
          onValidationChange(isValid);
        }
        setTouched(true); // Marcar como touched na montagem para mostrar erros se houver
        isValidatingRef.current = false;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleFieldChange = (
    fieldName: string,
    fieldValue: any,
    fieldId?: string
  ) => {
    // Evitar atualizações desnecessárias
    if (multiline) {
      const currentValue = itemsArray[activeItemIndex]?.[fieldName];
      const currentIdValue = fieldId
        ? itemsArray[activeItemIndex]?.[fieldId]
        : undefined;

      // Se o valor não mudou, não faz nada
      if (
        currentValue === fieldValue &&
        (!fieldId || currentIdValue === fieldValue)
      ) {
        return;
      }

      setTouched(true); // Mark as touched when any field changes

      const newItems = [...itemsArray];
      newItems[activeItemIndex] = {
        ...(newItems[activeItemIndex] || {}),
        [fieldName]: fieldValue,
        // Se tiver _id, salvar também com essa chave
        ...(fieldId ? { [fieldId]: fieldValue } : {}),
      };
      onChange(newItems);

      // Validar após a mudança para atualizar o estado de validação
      setTimeout(() => {
        isCurrentItemValid();
      }, 0);
    } else {
      const currentValue = (value as Record<string, any>)[fieldName];
      const currentIdValue = fieldId
        ? (value as Record<string, any>)[fieldId]
        : undefined;

      // Se o valor não mudou, não faz nada
      if (
        currentValue === fieldValue &&
        (!fieldId || currentIdValue === fieldValue)
      ) {
        return;
      }

      setTouched(true); // Mark as touched when any field changes

      onChange({
        ...(value as Record<string, any>),
        [fieldName]: fieldValue,
        // Se tiver _id, salvar também com essa chave
        ...(fieldId ? { [fieldId]: fieldValue } : {}),
      });

      // Validar após a mudança para atualizar o estado de validação
      setTimeout(() => {
        isCurrentItemValid();
      }, 0);
    }
  };

  const handleAddItem = () => {
    // Limpar a lista de campos inválidos
    setInvalidFields([]);

    const newItems = [...itemsArray, {}];
    onChange(newItems);
    setActiveItemIndex(newItems.length - 1);
  };

  const handleRemoveItem = (index: number) => {
    setDialogOpen(true);
  };

  const confirmRemoveItem = () => {
    if (multiline) {
      const newItems = itemsArray.filter((_, idx) => idx !== activeItemIndex);
      onChange(newItems.length > 0 ? newItems : [{}]);

      // Adjust active index if needed
      if (activeItemIndex >= newItems.length) {
        setActiveItemIndex(Math.max(0, newItems.length - 1));
      }
    }
    setDialogOpen(false);
  };

  const handleSelectItem = (index: number) => {
    // Clear invalid fields when changing tabs
    setInvalidFields([]);
    setActiveItemIndex(index);
  };

  // Check if field is required and empty
  const isFieldInError = (field: ProcessRoleFieldAdditionalData): boolean => {
    const activeItem = multiline
      ? itemsArray[activeItemIndex] || {}
      : (value as Record<string, any>);
    const fieldName = field.name || "";
    const fieldId = field._id || "";

    // Verificar se o campo é obrigatório e está na lista de campos inválidos
    return (
      touched &&
      field.required === true &&
      (invalidFields.includes(fieldName) || invalidFields.includes(fieldId))
    );
  };

  // Get input class name based on error state
  const getInputClassName = (field: ProcessRoleFieldAdditionalData): string => {
    const baseClass =
      "w-full placeholder:text-background/60 border-0 border-b rounded-none focus-visible:ring-0";
    const errorClass = isFieldInError(field)
      ? "border-b-red-500"
      : "border-b-background";
    return `${baseClass} ${errorClass}`;
  };

  // Get active item
  const activeItem = multiline
    ? itemsArray[activeItemIndex] || {}
    : (value as Record<string, any>);

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>, fieldId?: string) => {
    const cleanedValue = e.target.value.replace(/\D/g, "");
    const maskedValue = maskCPF(cleanedValue);
    handleFieldChange("cpf", maskedValue, fieldId);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>, fieldId?: string) => {
    const cleanedValue = e.target.value.replace(/\D/g, "");
    const maskedValue = maskPhone(cleanedValue);
    handleFieldChange("phone", maskedValue, fieldId);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>, fieldId?: string) => {
    const emailValue = e.target.value;
    handleFieldChange("email", emailValue, fieldId);
  };

  return (
    <>
      <h1 className="text-background font-bold text-sm sm:text-2xl mb-2.5 sm:mb-4">
        {label || processRole.name || "Personagem"}
      </h1>
      {(description || processRole.description) && (
        <h2 className="text-grey-light text-sm sm:text-base mb-6 sm:mb-8">
          {description || processRole.description}
        </h2>
      )}

      <p className="text-grey-light text-xs sm:text-sm mb-4 italic">
        {multiline
          ? `Adicione um ou mais itens do tipo ${processRole.name}`
          : `Preencha os dados do ${processRole.name}`}
      </p>

      {/* Tab navigation for multiple items */}
      {multiline && (
        <ProcessRoleTabNavigation
          items={itemsArray}
          activeIndex={activeItemIndex}
          onSelectIndex={handleSelectItem}
          onAddItem={handleAddItem}
          onRemoveItem={handleRemoveItem}
          roleName={processRole.name || "Personagem"}
        />
      )}

      {/* Show active item name if there are multiple items */}
      {multiline && itemsArray.length > 1 && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-background text-sm sm:text-base">
            {processRole.name} {activeItemIndex + 1}
          </p>
          {/* Optional: Add item-specific actions here */}
        </div>
      )}

      {/* Form fields */}
      <div className="flex flex-col justify-center text-xs sm:text-sm gap-4 w-full">
        <div className="flex flex-col gap-6">
          {/* Base fields */}
          <div className="flex flex-col gap-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              Nome
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Input
              type="text"
              value={activeItem.name || ""}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              className={getInputClassName({ name: "name", required: true } as ProcessRoleFieldAdditionalData)}
              placeholder="Digite o nome"
            />
            {touched && !activeItem.name && (
              <span className="text-red-500 text-xs mt-1">
                Campo obrigatório
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              CPF
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Input
              type="text"
              value={activeItem.cpf || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCPFChange(e)}
              className={getInputClassName({ name: "cpf", required: true } as ProcessRoleFieldAdditionalData)}
              placeholder="Digite o CPF"
              maxLength={14}
              inputMode="numeric"
            />
            {touched && !isValidCPF(activeItem.cpf ?? null) && (
              <span className="text-red-500 text-xs mt-1">
                CPF inválido
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              Email
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Input
              type="email"
              value={activeItem.email || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEmailChange(e)}
              className={getInputClassName({ name: "email", required: true } as ProcessRoleFieldAdditionalData)}
              placeholder="Digite o email"
            />
            {touched && !isValidEmail(activeItem.email ?? null) && (
              <span className="text-red-500 text-xs mt-1">
                Email inválido
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              Celular
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Input
              type="text"
              value={activeItem.phone || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePhoneChange(e)}
              className={getInputClassName({ name: "phone", required: true } as ProcessRoleFieldAdditionalData)}
              placeholder="Digite o celular"
              maxLength={15}
              inputMode="numeric"
            />
            {touched && !isValidPhone(activeItem.phone ?? null) && (
              <span className="text-red-500 text-xs mt-1">
                Telefone inválido
              </span>
            )}
          </div>

          {/* Additional fields */}
          {processRole.additionalData?.map((field: ProcessRoleFieldAdditionalData) => (
            <div
              key={field.name || field._id}
              className="flex flex-col gap-2"
            >
              <label className="text-primary font-bold text-sm sm:text-base px-0">
                {field.name}
                {field.required && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              <Input
                type={
                  field.type === 'number' ? 'number' : 
                  field.type === 'date' ? 'date' : 
                  field.type === 'email' ? 'email' : 
                  'text'
                }
                value={activeItem[field.name || ""] || activeItem[field._id || ""] || ""}
                onChange={(e) => handleFieldChange(field.name || "", e.target.value, field._id)}
                className={getInputClassName(field)}
                placeholder={`Digite ${field.name?.toLowerCase()}`}
              />
              {isFieldInError(field) && (
                <span className="text-red-500 text-xs mt-1">
                  Campo obrigatório
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {videoUrl && (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              className="flex gap-2 items-center bg-background/10 rounded-md w-fit px-4 py-2 mt-4"
            >
              <Tutorial fill="var(--background)" />
              <span>Ver vídeo explicativo</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="aspect-video min-w-2/3 bg-black/90 rounded-2xl border border-gray-800 text-grey-light">
            <div
              style={{
                left: 0,
                width: "100%",
                height: 0,
                position: "relative",
                paddingBottom: "56.25%",
              }}
            >
              <iframe
                src={videoUrl}
                style={{
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  border: 0,
                }}
                allowFullScreen
                scrolling="no"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;"
              ></iframe>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Diálogo de confirmação */}
      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={confirmRemoveItem}
        title="Confirmar Remoção"
        content={`Tem certeza de que deseja remover ${processRole.name} ${
          activeItemIndex + 1
        }?`}
      />
    </>
  );
};